import { Box, CollapsibleIconButton, Grid, Stack, Typography } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { useState } from 'react';
import { DEFAULT_QUERY_RESOLUTION, QueryOptionsDefinition } from '@/core';
import { ResolutionEditor } from '../ResolutionEditor';
import { TimeOptionsEditor, TimeOptionsEditorProps } from '../TimeOptionsEditor';

export function QueryOptionsEditor({
  queryOptions,
  onQueryOptionsChange,
}: {
  queryOptions?: QueryOptionsDefinition;
  onQueryOptionsChange: (queryOptions: QueryOptionsDefinition) => void;
}) {
  // Shows summary of selected options when accordion is closed.
  const selectedOptionsSummary = (
    <Stack direction={'row'} spacing={1}>
      <div>Resolution: {queryOptions?.resolution ?? DEFAULT_QUERY_RESOLUTION}</div>
      <div>Time: {queryOptions?.time?.start ?? 'Dashboard time'}</div>
    </Stack>
  );

  const handleResolutionChange = (newResolution: QueryOptionsDefinition['resolution']) => {
    onQueryOptionsChange(
      produce(queryOptions ?? {}, (draft) => {
        draft.resolution = newResolution;
      })
    );
  };

  const handleTimeOptionsChange: TimeOptionsEditorProps['onTimeChange'] = (newTime) => {
    onQueryOptionsChange(
      produce(queryOptions ?? {}, (draft) => {
        draft.time = newTime;
      })
    );
  };

  return (
    <CollapseQueryOptions title="Query settings" titleSummary={selectedOptionsSummary}>
      {/* maxWidth used to prevent horizontal scrollbar from showing */}
      <Grid container columnSpacing={4} sx={{ maxWidth: '100%' }}>
        <Grid item xs={4}>
          <Typography variant="h4" color="text.secondary">
            Options
          </Typography>
          <ResolutionEditor resolution={queryOptions?.resolution} onResolutionChange={handleResolutionChange} />
        </Grid>
        <Grid item xs={4}>
          <TimeOptionsEditor time={queryOptions?.time} onTimeChange={handleTimeOptionsChange} />
        </Grid>
      </Grid>
    </CollapseQueryOptions>
  );
}

function CollapseQueryOptions(props: {
  title: React.ReactNode;
  titleSummary?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { title, titleSummary } = props;
  const [isOptionsCollapsed, setIsOptionsCollapsed] = useState(true);

  return (
    <Stack spacing={1} mb={2}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ cursor: 'pointer' }}
        onClick={() => setIsOptionsCollapsed(!isOptionsCollapsed)}
      >
        <CollapsibleIconButton collapsed={isOptionsCollapsed} />
        <Typography variant="h4">{title}</Typography>
        {isOptionsCollapsed && titleSummary && (
          <Box sx={(theme) => ({ color: theme.palette.text.secondary, pl: 1, fontSize: (t) => t.typography.body2 })}>
            {titleSummary}
          </Box>
        )}
      </Stack>
      {!isOptionsCollapsed && props.children}
    </Stack>
  );
}
